import { type PropsWithChildren, useMemo } from 'react';
import { useIsMounted } from '@toss/react';
import { EnterTracking } from '@/components/EnterTracking';
import { useUser } from '@/lib/auth';
import { clearReferrer } from '@/shared/features/enter-referrer-tracking';
import { useBuildInfoContext } from '@/views/build/context/BuildInfoProvider';
import { captureBuildEntryPoint } from '../utils/buildEntryPointStore';
import { getBuildTrackingProperties } from '../utils/getBuildTrackingProperties';
interface SafeEnterTrackingProps extends PropsWithChildren {
  /** 트래킹할 이벤트 이름 */
  eventName: string;
}

/**
 * SafeEnterTracking: SSR-safe + Race-condition-free tracking wrapper
 *
 * 특징:
 * 1. SSR 시 children만 반환 (tracking 없음)
 * 2. Client-side에서만 EnterTracking 렌더링
 * 3. Property는 동기 함수로 주입 (useEffect 없음)
 * 4. Race condition 방지 보장
 *
 * @example
 * ```tsx
 * <SafeEnterTracking eventName="enter_build">
 *   <div>Build Page Content</div>
 * </SafeEnterTracking>
 * ```
 */
export const SafeEnterTracking = ({
  eventName,
  children
}: SafeEnterTrackingProps) => {
  const buildInfo = useBuildInfoContext();
  const isMounted = useIsMounted();
  const {
    user
  } = useUser();
  const isAuthor = useMemo(() => {
    if (!buildInfo.originQuizSet?.authorId || !user) return false;
    return buildInfo.originQuizSet?.authorId === user?.quizUserId;
  }, [buildInfo.originQuizSet?.authorId, user]);

  // SSR이거나 hydration 전: children만 반환
  if (!isMounted) {
    return <>{children}</>;
  }
  const eventProperties = getBuildTrackingProperties(buildInfo);
  return <EnterTracking eventName={eventName} eventProperties={{
    ...eventProperties,
    is_author: isAuthor
  }} onTrack={() => {
    // 지우기 **전에** 옮겨 둔다. 저장은 한참 뒤라 그때 다시 읽으면 비어 있다.
    captureBuildEntryPoint();
    clearReferrer();
  }} isReady={true} data-sentry-element="EnterTracking" data-sentry-component="SafeEnterTracking" data-sentry-source-file="SafeEnterTracking.tsx">
      {children}
    </EnterTracking>;
};